import { useTranslation } from '@pancakeswap/localization'
import { Currency } from '@pancakeswap/sdk'
import { Position } from '@pancakeswap/v3-sdk'
import FormattedCurrencyAmount from '@/components/FormattedCurrencyAmount/FormattedCurrencyAmount'
import { DoubleCurrencyLogo } from '@/components/Logo'
import { RangePriceSection } from '@/components/RangePriceSection'
import { Bound } from '@/config/constants/types'
import { useStablecoinPrice } from '@/hooks/useStablecoinPrice'
import { formatTickPrice } from '@/hooks/v3/utils/formatTickPrice'
import { ReactNode, useCallback, useState } from 'react'
import { formatPrice } from '@/utils/formatCurrencyAmount'
import { unwrappedToken } from '@/utils/wrappedCurrency'
import { RangeTag } from '@/components/RangeTag'
import { Typography } from '@/components/ui/typography'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { CurrencyLogo } from '@pancakeswap/widgets-internal'

export const PositionPreview = ({
  position,
  title,
  inRange,
  baseCurrencyDefault,
  ticksAtLimit,
}: {
  position: Position
  title?: ReactNode
  inRange: boolean
  baseCurrencyDefault?: Currency | undefined | null
  ticksAtLimit: { [bound: string]: boolean | undefined }
}) => {
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation()

  const currency0 = unwrappedToken(position.pool.token0)
  const currency1 = unwrappedToken(position.pool.token1)

  // track which currency should be base
  const [baseCurrency, setBaseCurrency] = useState(
    baseCurrencyDefault
      ? baseCurrencyDefault === currency0
        ? currency0
        : baseCurrencyDefault === currency1
        ? currency1
        : currency0
      : currency0,
  )

  const sorted = baseCurrency === currency0
  const quoteCurrency = sorted ? currency1 : currency0

  const price = sorted ? position.pool.priceOf(position.pool.token0) : position.pool.priceOf(position.pool.token1)

  const priceLower = sorted ? position.token0PriceLower : position.token0PriceUpper.invert()
  const priceUpper = sorted ? position.token0PriceUpper : position.token0PriceLower.invert()

  const price0 = useStablecoinPrice(position.pool.token0 ?? undefined, { enabled: !!position.amount0 })
  const price1 = useStablecoinPrice(position.pool.token1 ?? undefined, { enabled: !!position.amount1 })

  const handleRateChange = useCallback(() => {
    setBaseCurrency(quoteCurrency)
  }, [quoteCurrency])

  const removed = typeof position?.liquidity === 'bigint' && position?.liquidity === 0n

  if (!currency0 || !currency1) {
    return <></>
  }

  return (
    <div>
      <div className={'flex justify-between mb-4'}>
        <div className={'flex items-center gap-2'}>
          <DoubleCurrencyLogo currency0={currency0 ?? undefined} currency1={currency1 ?? undefined} />
          <div>
            <div className="flex items-center">
              <Typography>
                {currency0?.symbol}-{currency1?.symbol}
              </Typography>
              <Badge variant={'secondary'}>{position?.pool?.fee / 10000}%</Badge>
            </div>
            <RangeTag removed={removed} outOfRange={!inRange} />
          </div>
        </div>
      </div>

      <div className={'bg-secondary p-4 rounded-2xl'}>
        <div className={'flex flex-col gap-4'}>
          <div className={'flex justify-between'}>
            <div className={'flex items-center gap-2'}>
              <CurrencyLogo currency={currency0} />
              <Typography>{currency0?.symbol}</Typography>
            </div>
            <div>
              <Typography>
                <FormattedCurrencyAmount currencyAmount={position.amount0} />
              </Typography>
              <div className={'flex flex-column justify-end'}>
                <Typography variant={'tip'}>
                  {position.amount0 && price0
                    ? `~$${price0.quote(position.amount0?.wrapped).toFixed(2, { groupSeparator: ',' })}`
                    : ''}
                </Typography>
              </div>
            </div>
          </div>
          <div className={'flex justify-between'}>
            <div className={'flex items-center gap-2'}>
              <CurrencyLogo currency={currency1} />
              <Typography>{currency1?.symbol}</Typography>
            </div>
            <div>
              <Typography>
                <FormattedCurrencyAmount currencyAmount={position.amount1} />
              </Typography>
              <div className={'flex flex-column justify-end'}>
                <Typography variant={'tip'}>
                  {position.amount1 && price1
                    ? `~$${price1.quote(position.amount1?.wrapped).toFixed(2, { groupSeparator: ',' })}`
                    : ''}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={'mt-6'}>
        <div className={'flex justify-between mb-2'}>
          {title ? <h6>{title}</h6> : <div />}
          <div className={'flex justify-center items-center gap-2'}>
            <Typography variant={'small'}>{t('View prices in')}</Typography>
            <Tabs value={baseCurrency?.symbol} onValueChange={() => handleRateChange()}>
              <TabsList>
                <TabsTrigger value={currency0?.symbol}>{currency0?.symbol}</TabsTrigger>
                <TabsTrigger value={currency1?.symbol}>{currency1?.symbol}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className={'flex gap-6 mb-6'}>
          <RangePriceSection
            width="48%"
            title={t('Min Price')}
            currency0={quoteCurrency}
            currency1={baseCurrency}
            price={formatTickPrice(priceLower, ticksAtLimit, Bound.LOWER, locale)}
          />
          <RangePriceSection
            width="48%"
            title={t('Max Price')}
            currency0={quoteCurrency}
            currency1={baseCurrency}
            price={formatTickPrice(priceUpper, ticksAtLimit, Bound.UPPER, locale)}
          />
        </div>
        <RangePriceSection
          title={t('Current Price')}
          currency0={quoteCurrency}
          currency1={baseCurrency}
          price={formatPrice(price, 6, locale)}
        />
      </div>
    </div>
  )
}
